import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

// Invisible Cloudflare Turnstile widget.
//
// Two ways to consume:
//   1. Reactive: pass `onToken` — fires whenever a new token is issued.
//   2. Imperative: call `ref.current.getToken()` — returns a Promise that
//      resolves with a fresh, unused token (or "" on hard failure). This
//      avoids the "first-click fails because token hasn't arrived yet"
//      race the reactive flow has.

let scriptLoaded = false;
let loadingPromise = null;

function loadScript() {
  if (scriptLoaded) return Promise.resolve();
  if (loadingPromise) return loadingPromise;
  loadingPromise = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => { scriptLoaded = true; resolve(); };
    s.onerror = () => resolve();
    document.head.appendChild(s);
  });
  return loadingPromise;
}

const TurnstileWidget = forwardRef(function TurnstileWidget({ siteKey, onToken }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  // Single in-flight "wait for token" promise, so concurrent getToken() calls share one execution.
  const pendingRef = useRef(null);
  const lastTokenRef = useRef("");

  const fireToken = (tok) => {
    lastTokenRef.current = tok || "";
    if (onToken) onToken(tok || "");
    if (pendingRef.current) {
      pendingRef.current.resolve(tok || "");
      pendingRef.current = null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!siteKey) return;
    loadScript().then(() => {
      if (cancelled || !window.turnstile || !containerRef.current) return;
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => { fireToken(token); },
          "error-callback": () => { fireToken(""); },
          "expired-callback": () => { fireToken(""); },
        });
        try { window.turnstile.execute(widgetIdRef.current); } catch { /* ignore */ }
      } catch { /* ignore */ }
    });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* ignore */ }
      }
      if (pendingRef.current) {
        pendingRef.current.resolve("");
        pendingRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  useImperativeHandle(ref, () => ({
    /**
     * Returns a fresh, never-yet-used token.
     * - If a token is already cached from auto-execute, returns it once then clears the cache.
     * - Otherwise triggers a new execute() and waits for the callback.
     * - Rejects (resolves with "") after `timeoutMs` if nothing arrives.
     */
    getToken: (timeoutMs = 8000) => {
      // If we already have an unconsumed token, hand it over and clear.
      if (lastTokenRef.current) {
        const tok = lastTokenRef.current;
        lastTokenRef.current = "";
        return Promise.resolve(tok);
      }
      // Reuse an in-flight promise if one is already waiting.
      if (pendingRef.current) return pendingRef.current.promise;
      let resolveFn;
      const promise = new Promise((resolve) => { resolveFn = resolve; });
      pendingRef.current = { promise, resolve: resolveFn };
      // Kick the widget — it's safe to call execute() multiple times.
      if (window.turnstile && widgetIdRef.current != null) {
        try { window.turnstile.execute(widgetIdRef.current); } catch { /* ignore */ }
      }
      // Failsafe timeout so the form never hangs.
      setTimeout(() => {
        if (pendingRef.current && pendingRef.current.promise === promise) {
          pendingRef.current.resolve("");
          pendingRef.current = null;
        }
      }, timeoutMs);
      return promise;
    },
    /** Force the widget to drop any cached token and request a fresh one. */
    reset: () => {
      lastTokenRef.current = "";
      if (window.turnstile && widgetIdRef.current != null) {
        try { window.turnstile.reset(widgetIdRef.current); } catch { /* ignore */ }
        try { window.turnstile.execute(widgetIdRef.current); } catch { /* ignore */ }
      }
    },
  }), []);

  return <div ref={containerRef} data-testid="turnstile-widget" style={{ display: "none" }} />;
});

export default TurnstileWidget;
